import { useMemo } from "react";
import { useViewTransform } from "./useViewTransform";

const SKU_COLS = ["shipping_sku", "vividly_product", "stick_sku"] as const;
export type SkuCol = (typeof SKU_COLS)[number];

// crossFilterOptions[fromCol][toCol] = { fromValue: [toValue, ...], ... }
type CrossFilterOptions = Record<string, Record<string, string[]>>;
type SkuMap = Record<SkuCol, CrossFilterOptions>;

/**
 * Returned by `useSkuMapping`. Call `convert(values, fromCol, toCol)` to map
 * a selection of sku values from one grain's column to the equivalent set in
 * another grain's column. Silently drops values that have no mapping.
 *
 * `ready` is false until the underlying `get_schema` fetch resolves — callers
 * should fall back (e.g. clear the filter) when invoking conversion before
 * the mapping is available, since `convert` will just return [] in that case.
 */
export interface SkuMapping {
  ready: boolean;
  convert: (values: string[], fromCol: SkuCol, toCol: SkuCol) => string[];
}

/**
 * Loads a tiny per-run mapping between sku grains (shipping_sku /
 * vividly_product / stick_sku) by calling `get_schema` configured to compute
 * only the cross-filter map for those three columns.
 *
 * Cardinality and unique-value caps are raised explicitly — the default 1000
 * cap would silently drop SKUs on larger catalogs and break the conversion.
 */
export function useSkuMapping(
  runSettings: string | null | undefined,
  datasetName: string,
  folder: "inputs" | "outputs" | "settings" = "outputs",
): SkuMapping {
  const initialParams = useMemo(
    () =>
      runSettings
        ? {
            datasetName,
            folder,
            filter_columns: SKU_COLS as unknown as string[],
            // Only the sku columns matter; skip unique-value collects for
            // everything else to keep the call cheap.
            filter_columns_only: true,
            // Catalog SKU counts can exceed the 1000 default; raise high
            // enough that the mapping isn't silently truncated.
            cardinality_threshold: 1_000_000,
            unique_values_cap: 0,
            // The mapping comes from crossFilterOptions — we don't need
            // uniqueValues per column on this call.
            include_unique_values: false,
          }
        : undefined,
    [runSettings, datasetName, folder],
  );

  const vt = useViewTransform({
    transformId: "get_schema",
    runSettings: runSettings || undefined,
    initialParams,
  });

  const skuMap = useMemo<SkuMap | null>(() => {
    const table = vt.arrowTable;
    if (!table || table.numRows === 0) return null;
    const nameCol = table.getChild("name");
    const cfoCol = table.getChild("crossFilterOptions");
    if (!nameCol || !cfoCol) return null;
    const out: Partial<SkuMap> = {};
    for (let i = 0; i < table.numRows; i++) {
      const name = String(nameCol.get(i) ?? "");
      if (!(SKU_COLS as readonly string[]).includes(name)) continue;
      const raw = cfoCol.get(i);
      if (raw === null || raw === undefined) continue;
      try {
        out[name as SkuCol] = JSON.parse(String(raw)) as CrossFilterOptions;
      } catch {
        // ignore malformed entry — convert() falls through to []
      }
    }
    return out as SkuMap;
  }, [vt.arrowTable]);

  return useMemo<SkuMapping>(() => {
    const ready = skuMap !== null;
    const convert = (values: string[], fromCol: SkuCol, toCol: SkuCol): string[] => {
      if (fromCol === toCol) return values;
      if (!skuMap) return [];
      const lookup = skuMap[fromCol]?.[toCol];
      if (!lookup) return [];
      const out = new Set<string>();
      for (const v of values) {
        const mapped = lookup[v];
        if (mapped) for (const m of mapped) out.add(m);
      }
      return Array.from(out).sort();
    };
    return { ready, convert };
  }, [skuMap]);
}
